/**
 * "Is a car free?" - type the car a customer asks for (a model, a make, a class or a plate):
 * which of ours are free now, when the rented ones come back, and - when a time is given - how
 * many are free by then once the open bookings for the same car are served.
 *
 *   Ready now            fleet status Ready
 *   Back <date>          rented: the Drop-off Date of its open contract
 *   Overdue since <date> rented and the drop-off has passed - free only once it is returned
 *   No return date       rented, but no open contract found for the plate
 * Airport T1 / T2 only: Business Bay's cars (monthly contracts) and Invygo's are not offered, nor
 * are cars in the workshop / held, sold or stolen.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "../../ui/Dialog/Dialog";
import { Field, Input, Select, FormGrid } from "../../ui/Field/Field";
import Icon from "../../ui/Icon";
import { ICONS } from "../../ui/icon-map";
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../ui/Table";
import { CellPlate, CellText, CellTag, CellId } from "../../ui/DataTable/cells";
import { loadOpenContracts, openContractsByPlate, parseWhen, plateKey } from "../../services/openContracts";
import { bookingState } from "../../services/bookingsService";
import logger from "../../utils/logger";
import { isGone } from "../../services/fleetStatus";
import { formatContractDateTime } from "../../utils/contractDateUtils";

const pad = (n) => String(n).padStart(2, "0");
// the typed "Needed from" time (not a contract date)
const fmt = (d) => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}` : "—");

const CONTRACT_NO_KEYS = ["Contract No.", "contract_no", "contractNo", "id"];
const CONTRACT_DATE_KEYS = { pick: ["Pick-up Date", "pickup_date", "pickupDate"], drop: ["Drop-off Date", "dropoff_date", "dropoffDate"] };
const firstOf = (row, keys) => { for (const k of keys) { const v = row && row[k]; if (v !== undefined && v !== null && String(v).trim() !== "") return v; } return ""; };
/**
 * An open contract's Pick-up / Drop-off Date to show (which: 'pick' | 'drop'): "dd/mm/yyyy HH:MM", or the
 * date alone when the contract has no time (owner, 27 Sep 2026). The Date on `info` (openContracts) is
 * parsed in local time, so a date-only value would show an invented 00:00: the text is taken from the
 * contract row itself - the one with the same number whose date parses to the very same moment.
 * Display only: waits and comparisons stay on the Date.
 */
export function contractDateText(rows, info, which) {
    const when = info && info[which];
    if (!(when instanceof Date)) return "";
    const keys = CONTRACT_DATE_KEYS[which] || [];
    const row = (rows || []).find((r) => String(firstOf(r, CONTRACT_NO_KEYS)) === String(info.no)
        && parseWhen(firstOf(r, keys))?.getTime() === when.getTime());
    return formatContractDateTime(row ? firstOf(row, keys) : when);
}
export const carName = (c) => [c.make, c.model, c.year].map((x) => String(x || "").trim()).filter(Boolean).join(" ");
const branchKey = (b) => {
    const s = String(b || "").toLowerCase();
    if (s.includes("invygo")) return "Invygo";
    if (s.includes("terminal 2") || s.includes("t2")) return "Airport Terminal 2";
    if (s.includes("terminal 1") || s.includes("t1")) return "Airport Terminal 1";
    if (s.includes("business")) return "Business Bay";
    return "";
};
/** Only the airport branches rent by the day: Business Bay's cars are on monthly contracts and
 *  Invygo's are Invygo's, so neither is ever offered (nor are their bookings counted). */
export const BRANCH_OPTIONS = ["Airport Terminal 1", "Airport Terminal 2"];
const isAirportCar = (c) => !c.invygo_car && BRANCH_OPTIONS.includes(branchKey(c.branch));
const isAirportBooking = (b) => { const k = branchKey(b.pickup_branch); return !k || BRANCH_OPTIONS.includes(k); };

/** Typed words -> a matcher: every word must start a word of the text ("208" is not "2008"). */
export const queryMatcher = (query) => {
    const words = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return null;
    return (text) => {
        const hay = ` ${String(text || "").toLowerCase().replace(/[^a-z0-9؀-ۿ]+/g, " ")} `;
        return words.every((w) => hay.includes(` ${w.replace(/[^a-z0-9؀-ۿ]+/g, "")}`));
    };
};
const RANK = { ready: 0, rented: 1, overdue: 2, unknown: 3 };
const carText = (c) => `${c.make} ${c.model} ${c.year} ${c.category} ${c.plate_number} ${plateKey(c.plate_number)}`;

/**
 * The answer, from plain lists (tested on its own). `query`: what was typed; `needAt`: a Date or null
 * (null = "when is one free?").
 */
export function carAvailability({ cars, contracts, bookings, query, needAt = null, branch = "", now = new Date() }) {
    const match = queryMatcher(query);
    if (!match) return null;
    const openByPlate = openContractsByPlate(contracts);        // the open contract of each plate
    const at = needAt || now;
    const rows = (cars || [])
        .filter((c) => c && !isGone(c.status) && isAirportCar(c) && match(carText(c)) && (!branch || branchKey(c.branch) === branch))
        .map((c) => {
            const st = String(c.status || "").trim();
            const open = openByPlate.get(plateKey(c.plate_number));
            if (/^ready$|available/i.test(st)) return { c, state: "ready", freeAt: now, contract: null };
            if (!/rent/i.test(st)) return null;                       // workshop / held / anything else: not offered
            if (open && open.drop) return { c, state: open.drop < now ? "overdue" : "rented", freeAt: open.drop, contract: open };
            return { c, state: "unknown", freeAt: null, contract: open || null };
        })
        .filter(Boolean)
        .map((r) => ({ ...r, plate: r.c.plate_number, name: carName(r.c), branch: branchKey(r.c.branch) || r.c.branch || "",
            // the drop-off as shown (date + time when the contract has one)
            freeText: (r.state === "rented" || r.state === "overdue") ? (contractDateText(contracts, r.contract, "drop") || "—") : "",
            freeBy: r.state === "ready" || (r.state === "rented" && r.freeAt <= at) }))
        // ready first, then the rented ones by return date; overdue (an open contract whose drop-off
        // passed - often a monthly one never extended on Ejar) and unknown returns last
        .sort((a, b) => (Number(b.freeBy) - Number(a.freeBy)) || (RANK[a.state] - RANK[b.state])
            || ((a.freeAt ? a.freeAt.getTime() : Infinity) - (b.freeAt ? b.freeAt.getTime() : Infinity)));
    // open bookings not yet picked up that ask for the same car (up to the time, when one is given)
    const needs = (bookings || []).filter((b) => {
        if (!["open", "pending"].includes(bookingState(b)) || !isAirportBooking(b)) return false;
        const t = parseWhen(b.pickup_date);
        if (needAt && (!t || t > needAt)) return false;
        if (branch && branchKey(b.pickup_branch) !== branch) return false;
        return match(String(b.vehicle || ""));
    });
    const readyNow = rows.filter((r) => r.state === "ready").length;
    const free = rows.filter((r) => r.freeBy).length;
    const next = rows.find((r) => r.state === "rented" && !r.freeBy) || null;
    return { rows, total: rows.length, readyNow, free, booked: needs.length, left: free - needs.length, next };
}

const STATE_VIEW = {
    ready: { tone: "success", label: "Ready now" },
    rented: { tone: "info", label: "Rented" },
    overdue: { tone: "danger", label: "Overdue" },
    unknown: { tone: "warning", label: "Rented · no return date" },
};

export default function AvailabilityDialog({ open, onClose, fleetCars, bookings }) {
    const [query, setQuery] = useState("");
    const [when, setWhen] = useState("");               // "" = as soon as possible
    const [branch, setBranch] = useState("");
    const [contracts, setContracts] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);          // Try again re-reads the contracts
    const searchRef = useRef(null);                     // the cursor goes straight to the search

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoadFailed(false);
        loadOpenContracts()
            .then((rows) => { if (alive) setContracts(rows); })
            // no list, no answer: an empty list would show every rented car without its contract
            .catch((e) => { logger.warn("Availability: contracts not loaded", e); if (alive) { setContracts(null); setLoadFailed(true); } });
        return () => { alive = false; };
    }, [open, attempt]);

    const needAt = useMemo(() => { if (!when) return null; const d = new Date(when); return isNaN(d.getTime()) ? null : d; }, [when]);
    const res = useMemo(() => (contracts ? carAvailability({ cars: fleetCars, contracts, bookings, query, needAt, branch }) : null),
        [contracts, fleetCars, bookings, query, needAt, branch]);

    const answer = () => {
        if (!res.total) return { tone: "neutral", text: <>No car matching <b>“{query}”</b>{branch ? ` at ${branch}` : ""} that can be rented.</> };
        const bookedNote = res.booked > 0 ? <> · {res.booked} open booking(s) already ask for it{needAt ? " by then" : ""}</> : null;
        if (!needAt) {
            if (res.readyNow > 0) return { tone: "ok", text: <><b>{res.readyNow} ready now</b> of {res.total}{bookedNote}</> };
            return res.next
                ? { tone: "warn", text: <><b>None ready now</b> · the first one back <b>{res.next.freeText}</b> ({res.next.plate}){bookedNote}</> }
                : { tone: "bad", text: <><b>None ready now</b> and no return date on file{bookedNote}</> };
        }
        return res.left > 0
            ? { tone: "ok", text: <><b>Yes - {res.left} free by {fmt(needAt)}</b> · {res.free} of {res.total}{bookedNote}</> }
            : { tone: "bad", text: <><b>None left by {fmt(needAt)}</b>{bookedNote}{res.next ? <> · the next one is back <b>{res.next.freeText}</b> ({res.next.plate})</> : null}</> };
    };
    const TONE = { ok: "border-green-200 bg-green-50 text-green-900", warn: "border-amber-200 bg-amber-50 text-amber-900",
        bad: "border-red-200 bg-red-50 text-red-900", neutral: "border-gray-200 bg-gray-50 text-gray-700" };

    return (
        <Dialog open={open} onClose={onClose} size="lg" title="Is a car free?" className="ds-dialog--brand" initialFocusRef={searchRef}
            description="Type the car the customer wants: which of ours are free now and when the rented ones come back.">
            <FormGrid>
                <Field label="Car">
                    <Input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} prefix={<Icon glyph={ICONS.search} size="sm" />}
                        placeholder="208 · Pegas 2025 · Jetour · Economy · a plate" aria-label="Car to look for" />
                </Field>
                <Field label="Needed from" optional help="Empty: when is one free?">
                    <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
                </Field>
                <Field label="Branch">
                    <Select value={branch} onChange={(e) => setBranch(e.target.value)}>
                        <option value="">Both terminals</option>
                        {BRANCH_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </Select>
                </Field>
            </FormGrid>

            {!query.trim() && <p className="text-sm text-gray-500 mt-4">Type a model, a make, a class or a plate.</p>}
            {loadFailed && (
                <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 flex items-center justify-center gap-3">
                    <span>The contracts could not be read, so availability is unknown.</span>
                    <button type="button" className="font-semibold underline" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
                </div>
            )}
            {query.trim() && !contracts && !loadFailed && <p className="text-sm text-gray-500 mt-4">Reading the contracts…</p>}
            {res && (() => {
                const a = answer();
                return (
                    <div className="mt-4 flex flex-col gap-3">
                        <div role="status" className={`rounded-lg border px-4 py-3 text-sm ${TONE[a.tone]}`}>{a.text}</div>
                        {res.total > 0 && (
                            <TableContainer maxHeight="45vh" className="overflow-auto">
                                <Table sticky className="w-full">
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>Plate</TableHeader><TableHeader>Car</TableHeader><TableHeader>Branch</TableHeader>
                                            <TableHeader>Now</TableHeader><TableHeader>Free from</TableHeader><TableHeader>Contract</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {res.rows.map((r) => {
                                            const v = STATE_VIEW[r.state];
                                            return (
                                                <TableRow key={r.plate}>
                                                    <TableCell><CellPlate>{r.plate}</CellPlate></TableCell>
                                                    <TableCell><CellText>{r.name}</CellText></TableCell>
                                                    <TableCell><CellText>{r.branch}</CellText></TableCell>
                                                    <TableCell><CellTag tone={v.tone}>{v.label}</CellTag></TableCell>
                                                    <TableCell>
                                                        <span className={`ds-cell-date ${r.freeBy ? "text-green-700 font-semibold" : ""}`}>
                                                            {r.state === "ready" ? "Now" : r.state === "overdue" ? `Overdue since ${r.freeText}` : r.freeAt ? r.freeText : "—"}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{r.contract ? <span className="flex flex-col items-center"><CellId>{r.contract.no}</CellId><span className="ds-cell-muted">{r.contract.customer}</span></span> : <span className="ds-cell-empty">—</span>}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </div>
                );
            })()}
        </Dialog>
    );
}
